// Per-frame creature direction for the Zoo stage, as pure functions: every creature's pose is
// derived from the audible musical position, the notes of the pattern being heard and live hits
// (ported from the desktop app's Director, without its project/mixer store).
import { FIRST_ATMOS, isAtmosPad, laneSlot, NUM_LANE_SOUNDS, type NoteEvent, type Species } from "@eb/shared";
import { STREAM_BPM as BPM } from "../stream";
import { computePose, hitInfo, type HitInfo, type PadNote } from "./creature/creatureState";
import { restPose, type CreaturePose } from "./creature/pose";
import { NEUTRAL_TRAITS, type CreatureTraits } from "./creature/traits";
import type { AudiblePosition } from "./lib/clock";

/** Drum pads 0–15 + bass, harmony, lead. */
/** Creatures: 16 drums, 4 instruments and one for the whole atmos kit. */
export const NUM_SLOTS = FIRST_ATMOS + 1;

/** The creature a sound plays through (atmos pads: the kit's one; extra lanes: their instrument's). */
export const creatureOf = (pad: number) => (isAtmosPad(pad) ? FIRST_ATMOS : laneSlot(pad));

export interface ClockPosition {
  tick: number;
  lengthTicks: number;
  playing: boolean;
}

/** Per-slot note lists (sorted by tick, only notes inside the pattern). */
export function buildSlotNotes(events: readonly NoteEvent[], lengthTicks: number): PadNote[][] {
  const notes: PadNote[][] = Array.from({ length: NUM_SLOTS }, () => []);
  for (const e of events) {
    if (e.pad < 0 || e.pad >= NUM_LANE_SOUNDS || e.tick < 0 || e.tick >= lengthTicks) continue;
    // An extra instrument lane plays through its instrument's creature.
    notes[creatureOf(e.pad)].push({ tick: e.tick, velocity: e.velocity, length: e.length_ticks, note: e.note });
  }
  for (const list of notes) list.sort((a, b) => a.tick - b.tick);
  return notes;
}

/** Running transport position: counts pattern loops so 4-bar cheers and wrap-around hits work. */
export interface Transport {
  playing: boolean;
  lastTick: number;
  /** Ticks of completed loops (starts one pattern in, as if the loop had been playing). */
  base: number;
}

export function newTransport(): Transport {
  return { playing: false, lastTick: 0, base: 0 };
}

/** Advance the transport from a clock reading and return the audible position. */
export function advanceTransport(t: Transport, pos: ClockPosition): AudiblePosition {
  const len = Math.max(1, pos.lengthTicks);
  if (!pos.playing) {
    t.playing = false;
    return { tick: 0, transportTick: 0, playing: false, countIn: false };
  }
  const tick = ((pos.tick % len) + len) % len;
  if (!t.playing) {
    // Joining a loop that is already running (e.g. the River): treat the previous loop as heard.
    t.playing = true;
    t.base = len;
  } else if (tick < t.lastTick - len / 2) {
    t.base += len;
  }
  t.lastTick = tick;
  return { tick, transportTick: t.base + tick, playing: true, countIn: false };
}

/** Module-level live hits (zooHit), read by every mounted stage each frame. */
export interface LiveHits {
  at: Float64Array;
  vel: Float64Array;
}

export function newLiveHits(): LiveHits {
  return { at: new Float64Array(NUM_SLOTS), vel: new Float64Array(NUM_SLOTS) };
}

export function recordLiveHit(live: LiveHits, pad: number, velocity: number, nowMs: number): void {
  if (!Number.isInteger(pad) || pad < 0 || pad >= NUM_LANE_SOUNDS) return;
  const slot = creatureOf(pad);
  live.at[slot] = nowMs;
  live.vel[slot] = Math.max(1, Math.min(127, Math.round(velocity)));
}

export interface SlotFrameInput {
  nowMs: number;
  audible: AudiblePosition;
  lengthTicks: number;
  notes: readonly PadNote[];
  liveHitAt: number;
  liveHitVel: number;
  seed: number;
  /** 0–1 position in the cheer wave. */
  ripple: number;
  calm: boolean;
}

/** Hit info + pose for one creature this frame. Every note holds for its length (long notes hold). */
export function slotFrame(p: SlotFrameInput, out: CreaturePose = restPose()): { hit: HitInfo; pose: CreaturePose } {
  const hit = hitInfo({
    nowMs: p.nowMs,
    audible: p.audible,
    bpm: BPM,
    patternLength: p.lengthTicks,
    notes: p.notes,
    liveHitAt: p.liveHitAt,
    liveHitVel: p.liveHitVel,
    liveHeld: false,
    gate: true,
    sampleMs: 0,
  });
  computePose(
    {
      nowMs: p.nowMs,
      audible: p.audible,
      bpm: BPM,
      swing: 50,
      hit,
      muted: false,
      soloed: false,
      anySolo: false,
      tune: 0,
      pan: 0,
      chokeGroup: 0,
      mateHitAgoMs: Infinity,
      ripple: p.ripple,
      seed: p.seed,
      reducedMotion: p.calm,
    },
    out,
  );
  return { hit, pose: out };
}

/** Appearance traits without sound analysis: a gentle species/slot-based nudge from neutral. */
export function traitsForSlot(species: Species, slot: number): CreatureTraits {
  const t = { ...NEUTRAL_TRAITS };
  switch (species) {
    case "thumper":
    case "wobbler":
    case "boomer":
      t.size = 1.15;
      t.stature = -0.5;
      t.wobble = 0.5;
      break;
    case "chirper":
      t.size = 0.88;
      t.stature = 0.5;
      t.brightness = 0.8;
      break;
    case "snapper":
    case "clapper":
      t.spikiness = 0.6;
      break;
    case "glimmer":
      t.glow = 0.6;
      t.tail = 0.7;
      break;
    default:
      break;
  }
  if (slot === 16) t.stature = -0.6;
  if (slot === 17) t.width = 0.6;
  if (slot === 18) {
    t.stature = 0.5;
    t.brightness = 0.8;
  }
  return t;
}
